import { useEffect, useState } from "react";
import axios from "axios";

/**
 * Carga un avatar privado vía axios (que ya incluye JWT + X-Workspace-Id
 * gracias a APIClient.setAuthorization) y devuelve un object URL listo
 * para meter en `<img src>`. Si no existe (404) o no se puede cargar,
 * devuelve null y el componente debe caer a iniciales.
 *
 * No usa cache de browser de imágenes — cada montaje hace una request.
 * Para evitar tirones en listas grandes pasa `enabled=false` hasta que
 * el componente entre en viewport.
 *
 * `version` se puede usar para forzar re-fetch tras una subida (cambiar
 * de número desencadena un useEffect nuevo).
 *
 * @param {string|null} relativePath  ruta API del avatar (e.g. `/me/avatar`).
 *   null/empty → no se hace request, devuelve null.
 * @param {{ enabled?: boolean, version?: number }} [options]
 */
export function useAvatarBlobUrl(relativePath, options = {}) {
  const { enabled = true, version = 0 } = options;
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !relativePath) {
      setBlobUrl(null);
      return undefined;
    }
    let cancelled = false;
    let createdUrl = null;
    setLoading(true);
    axios
      .get(relativePath, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        // The response interceptor unwraps response.data, so `res` is the Blob directly
        const blob = res instanceof Blob ? res : res?.data;
        if (!blob || !(blob instanceof Blob) || blob.size === 0) {
          setBlobUrl(null);
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      })
      .catch(() => {
        if (cancelled) return;
        setBlobUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [relativePath, enabled, version]);

  return { blobUrl, loading };
}

export default useAvatarBlobUrl;
